import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  clamp,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {HTMLElement | null} */
  #zoomedMedia = null;

  /** The index of the media on screen, which Prev / Next step from. */
  #currentIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('pointermove', this.#followPointer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('pointermove', this.#followPointer);
  }

  /**
   * Clicking an image zooms it in to fill the screen, panning with the cursor; clicking again zooms back out.
   * Touch screens keep the drag-zoom wrapper, where a tap closes the dialog.
   *
   * @param {PointerEvent} event - The click event, targeting the media container.
   */
  handleMediaClick(event) {
    const media = event.target;
    const canZoom = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches;

    if (!(media instanceof HTMLElement) || !canZoom) return this.close();

    if (media === this.#zoomedMedia) return this.#resetZoom();

    this.#resetZoom();

    const image = media.querySelector('img');
    if (image?.naturalWidth) {
      // Scale the fitted image up until it covers the screen, and at least double it
      const { width, height } = media.getBoundingClientRect();
      const fit = Math.min(width / image.naturalWidth, height / image.naturalHeight);
      const cover = Math.max(width / image.naturalWidth, height / image.naturalHeight);
      media.style.setProperty('--zoom-scale', `${Math.max(2, cover / fit)}`);
    }

    this.loadHighResolutionImage(media);
    this.#zoomedMedia = media;
    this.#followPointer(event);
    media.classList.add('product-media-container--zoomed');
  }

  /**
   * Moves the zoom's origin to the cursor, so the zoomed image pans to whatever is under it.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #followPointer = (event) => {
    const media = this.#zoomedMedia;
    if (!media) return;

    const rect = media.getBoundingClientRect();
    const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);

    media.style.setProperty('--zoom-origin-x', `${x}%`);
    media.style.setProperty('--zoom-origin-y', `${y}%`);
  };

  #resetZoom() {
    this.#zoomedMedia?.classList.remove('product-media-container--zoomed');
    this.#zoomedMedia = null;
  }

  /**
   * Marks the media on screen. The one-image-at-a-time layout shows only that one, so it never
   * rests on a scroll position, which iOS could snap back to the first image after opening.
   *
   * @param {number} index - The index of the media on screen.
   */
  #markCurrent(index) {
    this.refs.media.forEach((item, i) => item.toggleAttribute('data-zoom-current', i === index));
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    this.#currentIndex = index;
    this.#markCurrent(index);

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }

      // A video opened from the gallery starts playing, to be watched in full
      const video = /** @type {(HTMLElement & { showDeferredMedia(): void, playMedia(): void }) | null} */ (
        targetImage?.querySelector('deferred-media') ?? null
      );
      if (video?.hasAttribute('data-media-loaded')) video.playMedia();
      else video?.showDeferredMedia();
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    /** @type {ReturnType<typeof fitToPicture> | undefined} */
    let picture;

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      picture = fitToPicture(targetImage);
      picture.element.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    picture?.element.style.removeProperty('view-transition-name');
    picture?.restore();

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    // Scrolling on to another image leaves the zoomed one behind
    if (this.#zoomedMedia && this.#zoomedMedia !== mostVisibleElement) this.#resetZoom();

    if (activeIndex >= 0) this.#currentIndex = activeIndex;

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    this.#resetZoom();

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    const picture = fitToPicture(mostVisibleElement);
    picture.element.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      picture.element.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    picture.restore();
    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.step(event.key === 'ArrowRight' ? 1 : -1);
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Shows the next or previous media, wrapping around at either end.
   *
   * @param {number} direction - 1 for next, -1 for previous.
   */
  step(direction) {
    const { length } = this.refs.media;
    if (length < 2) return;

    this.selectThumbnail((this.#currentIndex + direction + length) % length, { behavior: 'instant' });
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    this.#resetZoom();

    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#currentIndex = index;
    this.#markCurrent(index);

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Shrinks a media's image or video, fitted with `object-fit: contain`, to the picture it shows. A view transition then
 * morphs the picture itself, not its letterboxed box, which would land smaller than the gallery slide and then jump.
 *
 * @param {HTMLElement} media - The media container in the dialog.
 * @returns {{ element: HTMLElement, restore: () => void }} The element to name for the transition, and a function to undo the sizing.
 */
function fitToPicture(media) {
  const picture = media.querySelector('deferred-media[data-media-loaded] video') ?? media.querySelector('img');
  const unchanged = { element: media, restore: () => {} };

  if (!(picture instanceof HTMLImageElement || picture instanceof HTMLVideoElement)) return unchanged;

  const isVideo = picture instanceof HTMLVideoElement;
  const naturalWidth = isVideo ? picture.videoWidth : picture.naturalWidth;
  const naturalHeight = isVideo ? picture.videoHeight : picture.naturalHeight;

  if (!naturalWidth || !naturalHeight || getComputedStyle(picture).objectFit !== 'contain') return unchanged;

  // Layout sizes and offsets, which leave out any zoom transform still easing back
  const { offsetWidth, offsetHeight, offsetLeft, offsetTop } = picture;
  const scale = Math.min(offsetWidth / naturalWidth, offsetHeight / naturalHeight);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;

  picture.style.setProperty('flex', 'none');
  picture.style.setProperty('width', `${width}px`);
  picture.style.setProperty('height', `${height}px`);

  // Keep the smaller box centered where the picture was, wherever the layout put it
  const x = offsetLeft + (offsetWidth - width) / 2 - picture.offsetLeft;
  const y = offsetTop + (offsetHeight - height) / 2 - picture.offsetTop;
  picture.style.setProperty('translate', `${x}px ${y}px`);

  return {
    element: picture,
    restore: () => {
      for (const property of ['flex', 'width', 'height', 'translate']) picture.style.removeProperty(property);
    },
  };
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
